/**
 * Gerarchia della barra azioni (prototipo `Centro Ticket.dc.html` L192-L223):
 * un primario, i secondari, il resto nel menu overflow (L214-L216 per le voci,
 * L608-L615 per le etichette esatte). Risolve il rilievo §11.2 dell'inventario
 * (7 bottoni indifferenziati in fila). Le condizioni sono quelle di
 * `TicketDetail.tsx:529-625`, estratte in una funzione pura e testabile.
 */
import { STATUS_TRANSITIONS } from '../constants';
import type { AssigneeRef, ViewerRole } from '../../../services/supportApi';

export type S3ActionId =
  | 'take'
  | 'reply'
  | 'resolve'
  | 'status'
  | 'transfer'
  | 'assignees'
  | 'merge'
  | 'ignoreSender'
  | 'setRequester'
  | 'close'
  | 'delete'
  | 'knowledge'
  | 'details';

/**
 * Gruppo della voce nel menu ⋯ (v4, `Centro Ticket v4.dc.html` L1037-L1042).
 * Il v3 lo ignora: l'ordine dell'overflow resta quello di sempre.
 */
export type S3ActionGroup = 'lavorazione' | 'richiedente' | 'conoscenza' | 'definitive';

export interface S3Action {
  id: S3ActionId;
  label: string;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
  /** Solo sulle voci del menu overflow. */
  group?: S3ActionGroup;
}

export interface S3ActionCtx {
  status: string;
  /** Il ticket e gia assegnato all'operatore corrente (vedi `isMine`). */
  mine: boolean;
  canHandle: boolean;
  canTransfer: boolean;
  canSetRequester: boolean;
  /**
   * Cambio del richiedente gia attribuito (`*.change_requester`, Task 23/C).
   * Se il ticket aspetta ancora l'attribuzione vale `canSetRequester` e la voce
   * resta «Imposta richiedente»: mai due voci. Assente = false.
   */
  canChangeRequester?: boolean;
  canIgnoreSender: boolean;
  /**
   * Chiusura diretta e silenziosa (`supporttickets.close` /
   * `associativa.supporto.close`). Indipendente da `canHandle`: e un permesso a
   * se. Assente = false.
   */
  canClose?: boolean;
  /**
   * Eliminazione definitiva (`supporttickets.delete`, solo admin). Vale in
   * qualsiasi stato, anche sui ticket chiusi. Assente = false.
   */
  canDelete?: boolean;
  /**
   * «Crea scheda di conoscenza da questo ticket» (Task 29, `supportknowledge.manage`
   * / `associativa.supporto.knowledge_manage`). Vale in qualsiasi stato, anche
   * sui ticket chiusi, ma solo per i componenti del canale. Assente = false.
   */
  canCreateKnowledge?: boolean;
  narrow: boolean;
  /**
   * Il pannello Contesto sta nel cassetto "Dettagli". Sotto i 1024px e sempre
   * cosi (ruling 2), ma l'area rappresentante lo tiene nel cassetto a QUALUNQUE
   * larghezza (ruling 13: `.assoc-app .page` e largo 1180px): senza questo
   * campo il bottone "Dettagli" — l'unico modo di aprirlo — sparirebbe sopra i
   * 1024px. Assente = si segue `narrow`, che e il comportamento dell'admin.
   */
  contextInDrawer?: boolean;
  /**
   * L'operatore e componente del canale (o supervisore): `viewerIsMember` del
   * dettaglio. `false` = assegnatario esterno (Task 26): restano risposta, note
   * e stato; spariscono trasferimento, componenti, unione, richiedente, ignora
   * mittente, chiusura ed eliminazione (il backend risponde comunque 403).
   * Assente = true.
   */
  isGroupMember?: boolean;
  /**
   * Ruolo del visualizzatore (parte B, `ticket.viewerRole`). `participant` (e
   * `copy`, parte A): solo «Rispondi» (che diventa bozza) e «Dettagli»; niente
   * presa in carico, stato, menu ⋯. Assente = regole di sempre.
   */
  viewerRole?: ViewerRole;
  /**
   * Una chiamata al backend e in corso: tutte le azioni restano VISIBILI ma
   * disabilitate, come i bottoni attuali (`disabled={busy}`, TicketDetail.tsx:537).
   */
  busy?: boolean;
}

export interface S3ActionBar {
  primary: S3Action | null;
  secondary: S3Action[];
  overflow: S3Action[];
}

/** "Mio" = il mio id compare fra gli assegnatari del ticket. */
export const isMine = (assignees: AssigneeRef[] | undefined, selfId?: string): boolean =>
  Boolean(selfId) && (assignees || []).some((a) => String(a.actorId) === String(selfId));

/** Chi vede il ticket solo da invitato (o da canale in copia): legge, scrive note, propone bozze. */
export const isLimitedViewer = (role?: ViewerRole | null): boolean => role === 'participant' || role === 'copy';

export const buildActionBar = (input: S3ActionCtx): S3ActionBar => {
  if (isLimitedViewer(input.viewerRole)) {
    const inDrawerL = input.contextInDrawer ?? input.narrow;
    const secondaryL: S3Action[] = [];
    if (input.canHandle && input.status !== 'chiuso') secondaryL.push({ id: 'reply', label: 'Rispondi' });
    if (inDrawerL) secondaryL.push({ id: 'details', label: 'Dettagli' });
    return { primary: null, secondary: secondaryL.map((a) => (input.busy ? { ...a, disabled: true } : a)), overflow: [] };
  }
  // Assegnatario esterno: le capability riservate ai componenti del canale si
  // spengono qui, una volta sola, invece di condizionare ogni voce.
  const ctx: S3ActionCtx = input.isGroupMember === false
    ? {
        ...input,
        canTransfer: false,
        canSetRequester: false,
        canChangeRequester: false,
        canIgnoreSender: false,
        canClose: false,
        canDelete: false,
        canCreateKnowledge: false,
      }
    : input;
  const member = input.isGroupMember !== false;
  const closed = ctx.status === 'chiuso';
  const inDrawer = ctx.contextInDrawer ?? ctx.narrow;
  const transitions = STATUS_TRANSITIONS[ctx.status] || [];
  const secondary: S3Action[] = [];
  const overflow: S3Action[] = [];
  let primary: S3Action | null = null;

  if (ctx.canHandle && !closed) {
    // "Prendi in carico" resta SEMPRE visibile (parita con TicketDetail.tsx:533-542,
    // dove il bottone non sparisce a ticket assegnato): se e gia mio, disabilitato.
    primary = ctx.mine
      ? { id: 'take', label: 'Prendi in carico', disabled: true, title: 'Già in carico a te' }
      : { id: 'take', label: 'Prendi in carico' };

    secondary.push({ id: 'reply', label: 'Rispondi' });
    if (transitions.includes('risolto')) secondary.push({ id: 'resolve', label: 'Risolvi' });

    // Col pannello Proprieta in colonna stato e priorita si cambiano li: la
    // voce di menu comparirebbe due volte. Col pannello nel cassetto la voce
    // serve invece come scorciatoia per aprirlo sulle Proprieta.
    if (inDrawer) overflow.push({ id: 'status', label: 'Cambia stato / priorità', group: 'lavorazione' });
  }

  // "Trasferisci" dipende SOLO dal suo permesso (`supporttickets.transfer`):
  // parita con TicketDetail.tsx:581-590, dove la condizione e
  // `canTransfer && !isChiuso` e NON passa da `canHandle`.
  if (ctx.canTransfer && !closed) overflow.push({ id: 'transfer', label: 'Trasferisci a un gruppo', group: 'lavorazione' });

  if (ctx.canHandle && !closed && member) {
    overflow.push({ id: 'assignees', label: 'Aggiungi componente', group: 'lavorazione' });
    overflow.push({ id: 'merge', label: 'Unisci a un altro ticket', group: 'lavorazione' });
  }

  // Richiedente: una sola voce. Non dipende da `canHandle` (il permesso di
  // cambio basta da solo); l'etichetta dice se e la prima attribuzione.
  if (!closed) {
    if (ctx.canSetRequester) overflow.push({ id: 'setRequester', label: 'Imposta richiedente', group: 'richiedente' });
    else if (ctx.canChangeRequester) overflow.push({ id: 'setRequester', label: 'Cambia richiedente', group: 'richiedente' });
  }

  if (ctx.canHandle && !closed) {
    if (ctx.canIgnoreSender) {
      // Distruttiva e separata: chiude il ticket e sopprime il mittente.
      overflow.push({
        id: 'ignoreSender',
        label: 'Non pertinente: ignora mittente',
        danger: true,
        title: 'Chiude il ticket e non apre più ticket dalle email di questo mittente',
        // Nel v4 sta fra le definitive (in fondo), non fra le voci del
        // richiedente come nel prototipo L1038: chiude il ticket.
        group: 'definitive',
      });
    }
  }

  // Base di conoscenza: la bozza della scheda si ricava dalla conversazione,
  // quindi ha senso anche (soprattutto) sui ticket risolti e chiusi.
  if (ctx.canCreateKnowledge) {
    overflow.push({
      id: 'knowledge',
      label: 'Crea scheda di conoscenza da questo ticket',
      title: 'L’assistente AI prepara una bozza generica della scheda: va approvata nella Base di conoscenza',
      group: 'conoscenza',
    });
  }

  // Chiusura diretta: ultima voce del menu, rossa. Nessun avviso al
  // richiedente e nessun ritorno indietro, quindi passa da una conferma.
  if (ctx.canClose && !closed) {
    overflow.push({
      id: 'close',
      label: 'Chiudi ticket',
      danger: true,
      title: 'Chiude subito il ticket, senza avvisare il richiedente',
      group: 'definitive',
    });
  }

  // Eliminazione definitiva: SEMPRE l'ultima voce del menu, rossa e separata
  // (il bordo delle voci danger), in qualsiasi stato. Passa da una conferma
  // col numero del ticket digitato.
  if (ctx.canDelete) {
    overflow.push({
      id: 'delete',
      label: 'Elimina ticket',
      danger: true,
      title: 'Elimina per sempre il ticket con messaggi, note e allegati',
      group: 'definitive',
    });
  }

  // Col pannello Contesto nel cassetto serve una voce che lo apra anche sui
  // ticket chiusi, dove non c'e nessuna azione di lavorazione.
  if (inDrawer) secondary.push({ id: 'details', label: 'Dettagli' });

  // Durante una chiamata al backend nessuna azione e cliccabile: le voci non
  // spariscono (la barra non deve "ballare"), diventano solo inerti.
  const freeze = (a: S3Action): S3Action => (ctx.busy ? { ...a, disabled: true } : a);

  return {
    primary: primary ? freeze(primary) : null,
    secondary: secondary.map(freeze),
    overflow: overflow.map(freeze),
  };
};

export const ACTION_GROUP_ORDER: readonly S3ActionGroup[] = ['lavorazione', 'richiedente', 'conoscenza', 'definitive'];

/** Intestazioni dei gruppi (in maiuscolo via CSS, come L374 del prototipo). */
export const ACTION_GROUP_LABELS: Record<S3ActionGroup, string> = {
  lavorazione: 'Lavorazione',
  richiedente: 'Richiedente',
  conoscenza: 'Conoscenza',
  definitive: 'Definitive',
};

export interface S3ActionGroupBlock {
  group: S3ActionGroup;
  label: string;
  items: S3Action[];
}

/**
 * Menu ⋯ a gruppi (v4, N17): LAVORAZIONE / RICHIEDENTE / CONOSCENZA /
 * DEFINITIVE, gruppi vuoti omessi (l'assegnatario esterno ne vede meno, CT-69).
 * Nessuna voce si perde: una voce senza gruppo finisce fra le definitive se
 * distruttiva, altrimenti in lavorazione. Dentro il gruppo resta l'ordine di
 * `buildActionBar`, quindi le distruttive stanno in fondo e «Elimina» ultima.
 */
export const groupOverflow = (overflow: S3Action[]): S3ActionGroupBlock[] => {
  const groupOf = (a: S3Action): S3ActionGroup => a.group || (a.danger ? 'definitive' : 'lavorazione');
  return ACTION_GROUP_ORDER
    .map((group) => ({ group, label: ACTION_GROUP_LABELS[group], items: overflow.filter((a) => groupOf(a) === group) }))
    .filter((b) => b.items.length > 0);
};
